import {
  IReport,
  NodeReport,
  WorkflowInputs,
  WorkflowOutputs,
  WorkflowStatus,
} from '@flowgram.ai/runtime-interface';
import {
  injectable,
  inject,
  WorkflowDocument,
  Playground,
  WorkflowLineEntity,
  WorkflowNodeEntity,
  WorkflowNodeLinesData,
  Emitter,
  getNodeForm,
} from '@flowgram.ai/free-layout-editor';

import { WorkflowRuntimeClient } from '../browser-client';
import { saveWorkflowFun } from '../../../utils/common';
import {
  runTask,
  mockRunResponse,
  executeWorkflow,
  getWorkflowByTaskRecordId,
} from '../../../https/index';

const SYNC_TASK_REPORT_INTERVAL = 2000;

interface NodeRunningStatus {
  nodeID: string;
  status: WorkflowStatus;
  nodeResultLength: number;
}

const urlParams = new URLSearchParams(window.location.search);
const curTaskId = urlParams.get('taskId') || '';
const executorName = urlParams.get('executorName') || '';

@injectable()
export class WorkflowRuntimeService {
  @inject(Playground) playground: Playground;

  @inject(WorkflowDocument) document: WorkflowDocument;

  @inject(WorkflowRuntimeClient) runtimeClient: WorkflowRuntimeClient;

  private runningNodes: WorkflowNodeEntity[] = [];

  private taskID?: string;

  private syncTaskReportIntervalID?: ReturnType<typeof setInterval>;

  private reportEmitter = new Emitter<NodeReport>();

  private resetEmitter = new Emitter<{}>();

  public terminatedEmitter = new Emitter<{
    result?: {
      inputs: WorkflowInputs;
      outputs: WorkflowOutputs;
    };
    cancelled?: boolean;
  }>();

  private nodeRunningStatus: Map<string, NodeRunningStatus>;

  public onNodeReportChange = this.reportEmitter.event;

  public onReset = this.resetEmitter.event;

  public onTerminated = this.terminatedEmitter.event;

  public isFlowingLine(line: WorkflowLineEntity) {
    return this.runningNodes.some((node) =>
      node.getData(WorkflowNodeLinesData).inputLines.includes(line)
    );
  }

  public async taskRun(inputsString: string): Promise<void> {
    if (this.taskID) {
      await this.taskCancel();
    }
    if (!this.validate()) {
      return;
    }
    this.reset();
    try {
      // 调用执行任务工作流接口，获取taskRecordId
      const executeResponse = await executeWorkflow({ taskId: curTaskId, executorName });
      const { taskRecordId } = executeResponse;

      // 设置taskID用于后续轮询
      this.taskID = taskRecordId;

      // 启动轮询查询任务状态
      this.startPollingTaskStatus(taskRecordId, inputsString);
    } catch (error) {
      console.error('Task run failed:', error);
      this.terminatedEmitter.fire({ cancelled: false });
    }
  }

  public async taskCancel(): Promise<void> {
    if (!this.taskID) {
      return;
    }

    // 停止轮询
    if (this.syncTaskReportIntervalID) {
      clearInterval(this.syncTaskReportIntervalID);
      this.syncTaskReportIntervalID = undefined;
    }

    // 这里可以添加取消任务的API调用
    // await this.runtimeClient.TaskCancel({
    //   taskID: this.taskID,
    // });

    this.taskID = undefined;

    // 触发任务终止事件，通知UI更新状态（标记为取消）
    this.terminatedEmitter.fire({ cancelled: true });
  }

  private async validate(): Promise<boolean> {
    const allForms = this.document.getAllNodes().map((node) => getNodeForm(node));
    const formValidations = await Promise.all(allForms.map(async (form) => form?.validate()));
    const validations = formValidations.filter((validation) => validation !== undefined);
    const isValid = validations.every((validation) => validation);
    return isValid;
  }

  private reset(): void {
    this.taskID = undefined;
    this.nodeRunningStatus = new Map();
    this.runningNodes = [];
    if (this.syncTaskReportIntervalID) {
      clearInterval(this.syncTaskReportIntervalID);
    }
    this.resetEmitter.fire({});
  }

  private startPollingTaskStatus(taskRecordId: string, inputsString: string): void {
    // 清除之前的轮询
    if (this.syncTaskReportIntervalID) {
      clearInterval(this.syncTaskReportIntervalID);
    }

    // 启动新的轮询
    this.syncTaskReportIntervalID = setInterval(async () => {
      await this.syncTaskReport(taskRecordId, inputsString);
    }, SYNC_TASK_REPORT_INTERVAL);

    // 立即执行一次查询
    this.syncTaskReport(taskRecordId, inputsString);
  }

  private async syncTaskReport(taskRecordId: string, inputsString: string): Promise<void> {
    if (!this.taskID) {
      return;
    }
    if (!this.syncTaskReportIntervalID) {
      return;
    }

    try {
      // 调用新的查询接口
      const response = await getWorkflowByTaskRecordId({ taskRecordId });

      // 转换响应结果为节点报告并更新
      const reports: Record<string, NodeReport> = {};
      response.taskNodeRecords.forEach((node: any) => {
        if (!node.nodeId) {
          return;
        }

        reports[node.nodeId] = {
          id: node.nodeId,
          status: node.status as WorkflowStatus,
          snapshots: [
            {
              id: `${node.nodeId}-snapshot-0`,
              nodeID: node.nodeId,
              inputs: {},
              outputs: { config: node.config, resultMessage: node.resultMessage },
              data: node,
            },
          ],
          terminated:
            node.status === 'SUCCEEDED' || node.status === 'FAILED' || node.status === 'CANCELED',
          startTime: node.startTime || 0,
          timeCost: node.duration || 0,
        };
      });

      this.updateReport({ reports } as IReport);

      // 检查任务是否完成
      const taskRecord = response.taskRecord;
      const isTaskCompleted =
        taskRecord.status === 'SUCCEEDED' ||
        taskRecord.status === 'FAILED' ||
        taskRecord.status === 'CANCELED';

      if (isTaskCompleted) {
        // 任务完成，停止轮询
        clearInterval(this.syncTaskReportIntervalID);
        this.syncTaskReportIntervalID = undefined;

        // 触发任务完成事件
        this.terminatedEmitter.fire({
          result: {
            inputs: JSON.parse(inputsString) as WorkflowInputs,
            outputs: taskRecord,
          },
          cancelled: false,
        });
      }
    } catch (error) {
      console.error('Sync task report failed:', error);
      // 不要立即停止轮询，可能是网络临时问题
      // 只有在连续失败多次后才停止轮询
      // clearInterval(this.syncTaskReportIntervalID);
      // this.syncTaskReportIntervalID = undefined;
      // this.terminatedEmitter.fire({ cancelled: false });
    }
  }

  private updateReport(report: IReport): void {
    const { reports } = report;
    this.runningNodes = [];
    this.document.getAllNodes().forEach((node) => {
      console.log('node', node, node.id, reports);
      const nodeID = node.id;
      const nodeReport = reports[nodeID];
      if (!nodeReport) {
        return;
      }

      // 修复状态比较逻辑 - 将状态转换为字符串进行比较
      const statusStr = String(nodeReport.status).toUpperCase();
      if (statusStr === 'PROCESSING') {
        this.runningNodes.push(node);
      }

      const runningStatus = this.nodeRunningStatus.get(nodeID);
      const statusChanged = !runningStatus || nodeReport.status !== runningStatus.status;
      const snapshotChanged =
        !runningStatus || nodeReport.snapshots.length !== runningStatus.nodeResultLength;

      if (statusChanged || snapshotChanged) {
        this.nodeRunningStatus.set(nodeID, {
          nodeID,
          status: nodeReport.status,
          nodeResultLength: nodeReport.snapshots.length,
        });
        this.reportEmitter.fire(nodeReport);
        this.document.linesManager.forceUpdate();
      } else if (statusStr === 'PROCESSING') {
        // 对于正在处理的节点，持续触发报告更新
        this.reportEmitter.fire(nodeReport);
      } else if (statusStr === 'FAILED') {
        // 对于失败的节点，确保失败信息能够显示
        this.reportEmitter.fire(nodeReport);
      }
    });
  }
}
